import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Box,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Cart from './Cart';
import logo from '../assets/logo.png';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface LayoutProps {
  children: React.ReactNode;
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [cartOpen, setCartOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const isAdminRoute = location.pathname.startsWith('/admin');

  const cartItemsCount = useSelector((state: RootState) =>
    state.cart.items.reduce((total, item) => total + item.quantity, 0)
  );

  if (isAdminRoute) {
    return <>{children}</>;
  }

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: isHomePage ? 'rgba(26, 20, 18, 0.28)' : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: isHomePage ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(26,20,18,0.06)',
          color: isHomePage ? '#fff' : 'text.primary',
          transition: 'background-color 0.3s ease',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, md: 72 }, px: { xs: 1.5, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexGrow: 1,
              cursor: 'pointer',
              gap: 1.5,
            }}
            onClick={() => navigate('/')}
          >
            <Box
              component="img"
              src={logo}
              alt="Hello Gassy'z"
              sx={{
                height: { xs: 42, md: 52 },
                width: 'auto',
              }}
            />
            <Typography
              variant="h6"
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 600,
                fontSize: { xs: '1.05rem', sm: '1.25rem' },
                color: isHomePage ? '#fff' : 'primary.main',
                letterSpacing: '-0.02em',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Hello Gassy&apos;z
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!isHomePage && !isMobile && (
              <Button
                color="inherit"
                onClick={() => navigate('/')}
                sx={{ mr: 1, color: 'text.secondary' }}
              >
                Catalogue
              </Button>
            )}
            <IconButton
              onClick={() => setCartOpen(true)}
              aria-label="Panier"
              sx={{
                color: isHomePage ? '#fff' : 'primary.main',
                backgroundColor: isHomePage
                  ? 'rgba(255,255,255,0.12)'
                  : 'rgba(227, 30, 36, 0.08)',
                '&:hover': {
                  backgroundColor: isHomePage
                    ? 'rgba(255,255,255,0.2)'
                    : 'rgba(227, 30, 36, 0.16)',
                },
              }}
            >
              <Badge
                badgeContent={cartItemsCount}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    fontWeight: 700,
                  },
                }}
              >
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          marginTop: isHomePage ? 0 : '64px',
        }}
      >
        {children}
      </Box>

      <Cart open={cartOpen} onClose={() => setCartOpen(false)} />
    </>
  );
};

export default Layout;
